import { createReducer, on } from '@ngrx/store';
import { Departures } from 'app/core/enums/common.enum';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as NoveltiesSaleActions from './novelties-sales.actions';
import { NoveltiesSale } from './novelties-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface NoveltiesSalesState {
    sales: NoveltiesSale[];
    sale: NoveltiesSale | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialNoveltiesSalesState: NoveltiesSalesState = {
    sales: [],
    sale: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All NoveltiesSales Reducer
export const NoveltiesSalesReducer = createReducer(
    initialNoveltiesSalesState,

    // On Load NoveltiesSales
    on(NoveltiesSaleActions.loadNoveltiesSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(NoveltiesSaleActions.loadNoveltiesSalesSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: data,
    })),
    on(NoveltiesSaleActions.loadNoveltiesSalesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Clear
    on(NoveltiesSaleActions.clearNoveltiesSales, (state) => ({
        ...state,
        sales: [],
        sale: null,
        isLoading: false,
        error: null,
    })),

    // On Load Single NoveltiesSale
    on(NoveltiesSaleActions.loadNoveltiesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(NoveltiesSaleActions.loadNoveltiesSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sale: data,
    })),
    on(NoveltiesSaleActions.loadNoveltiesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(NoveltiesSaleActions.createNoveltiesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(NoveltiesSaleActions.createNoveltiesSaleSuccess, (state, { sale }) => ({
        ...state,
        isLoading: false,
        sales: ((): NoveltiesSale[] => {
            const sales = state.sales || [];
            let updateSales = [];
            sales.some((s) => s.location.id === sale.location.id)
                ? (updateSales = sales.map((_sale: NoveltiesSale) =>
                      _sale.location.id === sale.location.id
                          ? {
                                // Spread the old sale to keep old cash
                                // and credit card
                                ..._sale,
                                children: [..._sale.children, ...sale.children],
                                totals: sale.totals,
                            }
                          : _sale
                  ))
                : (updateSales = [...sales, sale]);

            return updateSales;
        })(),
    })),
    on(NoveltiesSaleActions.createNoveltiesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(NoveltiesSaleActions.updateNoveltiesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(NoveltiesSaleActions.updateNoveltiesSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: state.sales.map((s) =>
            s.location.id === data.location.id
                ? {
                      ...s,
                      children: s.children.map((child) =>
                          child.saleId === data.children[0].saleId
                              ? data.children[0]
                              : child
                      ),
                      totals: data.totals,
                  }
                : s
        ),
    })),
    on(NoveltiesSaleActions.updateNoveltiesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update Location Gross
    on(
        TotalGrossActions.updateTotalGrossSuccess,
        (state, { data, departure }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((s) =>
                s.location.id === data.location_id &&
                departure === Departures.NOVELTIES
                    ? {
                          ...s,
                          cash: data.cash,
                          credit_card: data.credit_card,
                          total_money: data.total_gross,
                          totals_local: {
                              cash: data.total_local.cash,
                              credit_card: data.total_local.credit_card,
                              count: s.totals_local.count,
                              amount: data.total_gross,
                          },
                      }
                    : s
            ),
        })
    ),

    // On Delete
    on(NoveltiesSaleActions.deleteNoveltiesSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        NoveltiesSaleActions.deleteNoveltiesSaleSuccess,
        (state, { deletedSales, remainingSales }) => ({
            ...state,
            isLoading: false,
            sales: (() => {
                if (remainingSales.length === 0) {
                    return state.sales.filter(
                        (sales: NoveltiesSale) =>
                            !sales.children.some(
                                (child) => child.saleId === deletedSales[0]
                            )
                    );
                } else {
                    console.warn(
                        'Remaining sales after deletion:',
                        remainingSales
                    );
                }
            })(),
        })
    ),
    on(NoveltiesSaleActions.deleteNoveltiesSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Sale Item
    on(NoveltiesSaleActions.deleteNoveltiesSaleItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        NoveltiesSaleActions.deleteNoveltiesSaleItemSuccess,
        (state, { deletedSaleItem, totals }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((_sale: NoveltiesSale) =>
                _sale.children.some((child) => child.saleId === deletedSaleItem)
                    ? {
                          ..._sale,
                          children: _sale.children.filter(
                              (child) => child.saleId !== deletedSaleItem
                          ),
                          totals: totals,
                      }
                    : _sale
            ),
        })
    ),
    on(
        NoveltiesSaleActions.deleteNoveltiesSaleItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    )
);
